import React, { useCallback, useEffect, useState } from "react";
import {
	Button,
	Chip,
	CircularProgress,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiRefresh, mdiCheckCircle, mdiAlertCircle, mdiCodeJson } from "@mdi/js";
import {
	BackofficeAdminServices,
	NotistackServices,
	McpAuditLogFilters,
	McpAuditLogResponse,
	PublicMcpAuditLogDTO,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

/**
 * EK-291 — Visualizador de Audit Log de chamadas MCP.
 * Filtros: mcp slug, tool, success, janela. Tabela paginada com expand
 * por row pra ver args/result preview.
 */

function fmtDuration(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(2)}s`;
}

function fmtBytes(b: number): string {
	if (b < 1024) return `${b}B`;
	if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
	return `${(b / (1024 * 1024)).toFixed(2)}MB`;
}

function fmtDate(iso: string): string {
	return new Date(iso).toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

const PAGE_SIZE = 50;

export const McpAuditPage: React.FC = () => {
	const [data, setData] = useState<McpAuditLogResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [expanded, setExpanded] = useState<string | null>(null);

	// Filtros
	const [mcpSlug, setMcpSlug] = useState("");
	const [toolName, setToolName] = useState("");
	const [successFilter, setSuccessFilter] = useState<"all" | "success" | "failed">("all");
	const [accountId, setAccountId] = useState("");
	const [offset, setOffset] = useState(0);

	const fetchData = useCallback(async () => {
		setLoading(true);
		const filters: McpAuditLogFilters = { limit: PAGE_SIZE, offset };
		if (mcpSlug.trim()) filters.mcpSlug = mcpSlug.trim();
		if (toolName.trim()) filters.toolName = toolName.trim();
		if (accountId.trim()) filters.accountId = accountId.trim();
		if (successFilter === "success") filters.success = true;
		if (successFilter === "failed") filters.success = false;

		try {
			const response = await BackofficeAdminServices.listMcpAuditLog(filters);
			setData(response);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar audit log");
		} finally {
			setLoading(false);
		}
	}, [mcpSlug, toolName, accountId, successFilter, offset]);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	const handleApplyFilters = () => {
		setOffset(0);
		void fetchData();
	};

	const handlePrev = () => {
		setOffset((o) => Math.max(0, o - PAGE_SIZE));
	};

	const handleNext = () => {
		if (data && offset + PAGE_SIZE < data.total) {
			setOffset((o) => o + PAGE_SIZE);
		}
	};

	const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>Audit Log MCP</h1>
					<p className={styles.subtitle}>
						EK-291 · Rastreamento padronizado de cada chamada MCP (payload sanitizado, duration, status).
					</p>
				</div>
				<div className={styles.actions}>
					<Button
						size="small"
						variant="outlined"
						onClick={() => void fetchData()}
						disabled={loading}
						startIcon={<Icon path={mdiRefresh} size={0.8} />}
					>
						Recarregar
					</Button>
				</div>
			</div>

			<div className={styles.filters}>
				<TextField
					size="small"
					placeholder="MCP slug (outlook, mscalendar, onedrive, ...)"
					value={mcpSlug}
					onChange={(e) => setMcpSlug(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<TextField
					size="small"
					placeholder="Tool name"
					value={toolName}
					onChange={(e) => setToolName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<TextField
					size="small"
					placeholder="Account ID"
					value={accountId}
					onChange={(e) => setAccountId(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<Select
					size="small"
					value={successFilter}
					onChange={(e) => setSuccessFilter(e.target.value as any)}
				>
					<MenuItem value="all">Todos</MenuItem>
					<MenuItem value="success">Sucesso</MenuItem>
					<MenuItem value="failed">Falha</MenuItem>
				</Select>
				<Button variant="contained" size="small" onClick={handleApplyFilters}>
					Filtrar
				</Button>
			</div>

			{loading && !data ? (
				<div className={styles.loadingState}>
					<CircularProgress size={32} />
				</div>
			) : !data || data.items.length === 0 ? (
				<div className={styles.empty}>
					<p>Nenhuma chamada MCP no audit log (com os filtros aplicados).</p>
				</div>
			) : (
				<>
					<div className={styles.summary}>
						<span>
							<strong>{data.total}</strong> total · página <strong>{currentPage}/{totalPages}</strong>
						</span>
						<div className={styles.pager}>
							<Button size="small" variant="outlined" onClick={handlePrev} disabled={offset === 0 || loading}>
								‹ Anterior
							</Button>
							<Button
								size="small"
								variant="outlined"
								onClick={handleNext}
								disabled={!data || offset + PAGE_SIZE >= data.total || loading}
							>
								Próxima ›
							</Button>
						</div>
					</div>

					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Quando</th>
									<th>Status</th>
									<th>MCP</th>
									<th>Tool</th>
									<th>Account</th>
									<th>Duration</th>
									<th>Args size</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((row) => (
									<RowItem
										key={row.id}
										row={row}
										expanded={expanded === row.id}
										onToggle={() => setExpanded((e) => (e === row.id ? null : row.id))}
									/>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</div>
	);
};

const RowItem: React.FC<{ row: PublicMcpAuditLogDTO; expanded: boolean; onToggle: () => void }> = ({
	row,
	expanded,
	onToggle,
}) => {
	return (
		<>
			<tr className={`${styles.row} ${row.success ? "" : styles.rowFailed}`} onClick={onToggle}>
				<td className={styles.colDate}>{fmtDate(row.createdAt)}</td>
				<td>
					<Chip
						size="small"
						icon={<Icon path={row.success ? mdiCheckCircle : mdiAlertCircle} size={0.7} />}
						label={row.success ? "OK" : "FAIL"}
						color={row.success ? "success" : "error"}
						variant={row.success ? "outlined" : "filled"}
					/>
				</td>
				<td className={styles.mono}>{row.mcpSlug}</td>
				<td className={styles.mono}>{row.toolName}</td>
				<td className={styles.colAccount} title={row.accountId}>
					{row.accountId.slice(0, 8)}…
				</td>
				<td className={styles.mono}>{fmtDuration(row.durationMs)}</td>
				<td className={styles.mono}>
					{fmtBytes(row.argsSizeBytes)}
					{row.argsTruncated && <span className={styles.truncatedFlag}> ✂</span>}
				</td>
				<td>
					<IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
						<Icon path={mdiCodeJson} size={0.7} />
					</IconButton>
				</td>
			</tr>
			{expanded && (
				<tr className={styles.expandedRow}>
					<td colSpan={8}>
						<div className={styles.expandedContent}>
							{row.errorMessage && (
								<div className={styles.errorBox}>
									<strong>Erro:</strong> {row.errorMessage}
									{row.errorCode && <span className={styles.errorCode}> (code: {row.errorCode})</span>}
								</div>
							)}
							<div className={styles.previewSection}>
								<h4>Args preview {row.argsTruncated && <span className={styles.truncatedLabel}>(truncado de {fmtBytes(row.argsSizeBytes)})</span>}</h4>
								<pre className={styles.json}>{JSON.stringify(row.argsPreview, null, 2)}</pre>
							</div>
							{row.resultPreview && (
								<div className={styles.previewSection}>
									<h4>Result preview</h4>
									<pre className={styles.json}>{JSON.stringify(row.resultPreview, null, 2)}</pre>
								</div>
							)}
							{(row.workspaceId || row.teamId || row.memberId || row.contractedMcpId || row.requestId) && (
								<div className={styles.idsRow}>
									{row.workspaceId && <Tooltip title="workspaceId"><span className={styles.idChip}>ws: {row.workspaceId.slice(0, 8)}…</span></Tooltip>}
									{row.teamId && <Tooltip title="teamId"><span className={styles.idChip}>team: {row.teamId.slice(0, 8)}…</span></Tooltip>}
									{row.memberId && <Tooltip title="memberId"><span className={styles.idChip}>member: {row.memberId.slice(0, 8)}…</span></Tooltip>}
									{row.contractedMcpId && <Tooltip title="contractedMcpId"><span className={styles.idChip}>mcp-contract: {row.contractedMcpId.slice(0, 8)}…</span></Tooltip>}
									{row.requestId && <Tooltip title="requestId"><span className={styles.idChip}>req: {row.requestId.slice(0, 16)}…</span></Tooltip>}
								</div>
							)}
						</div>
					</td>
				</tr>
			)}
		</>
	);
};
